import React from "react";
import styled from "styled-components";
import { useApolloClient } from "@apollo/react-hooks";
import { produce } from "immer";
import { remove } from "lodash";
import { Link } from "@sonder/ui/source/styled/fragments/Link";
import { useSegment } from "analytics/segment";
import { useStore as useAuthStore } from "auth/store";
import {
  CreateSubscriptionResponse,
  useCreateSubscription,
} from "core/queries/CreateSubscription";
import {
  DeleteSubscriptionResponse,
  useDeleteSubscription,
} from "core/queries/DeleteSubscription";
import { GetTaskResponse, TaskData, getTask } from "core/queries/GetTask";
import { NotificationResource } from "typings/globalGQLTypes";

const StyledLink = styled(Link)`
  align-self: center;
  margin-left: 0.3em;
`;

interface FollowTaskLinkProps {
  task: TaskData;
}

export const FollowTaskLink = ({ task }: FollowTaskLinkProps) => {
  const createSubscriptionEventName = "create_subscription";
  const deleteSubscriptionEventName = "delete_subscription";
  const taskId = task.id;

  const { state: authState } = useAuthStore();
  const userId = authState.user?.id;

  const client = useApolloClient();
  const { track } = useSegment();

  const updateCachedTask = (subscribed: boolean) => {
    const variables = { taskId };

    const baseState = client.readQuery<GetTaskResponse>({
      query: getTask,
      variables,
    });

    if (!baseState) {
      return;
    }

    client.writeQuery({
      data: produce(baseState, draftState => {
        const subscribers = draftState.task?.subscribers;
        if (!subscribers || !userId) {
          return;
        }

        if (subscribed) {
          subscribers.push({ __typename: "User", id: userId });
        } else {
          remove(subscribers, subscriber => {
            return subscriber.id === userId;
          });
        }
      }),
      query: getTask,
      variables,
    });
  };

  const onCreateSubscriptionCompleted = (data: CreateSubscriptionResponse) => {
    const success = data.createSubscription?.success;
    if (!success) {
      return;
    }

    updateCachedTask(true);
  };

  const [createSubscription, { loading: createSubscriptionLoading }] =
    useCreateSubscription({
      refetchQueries: [],
      onCompleted: onCreateSubscriptionCompleted,
      awaitRefetchQueries: false,
    });

  const onDeleteSubscriptionCompleted = (data: DeleteSubscriptionResponse) => {
    const success = data.deleteSubscription?.success;
    if (!success) {
      return;
    }

    updateCachedTask(false);
  };

  const [deleteSubscription, { loading: deleteSubscriptionLoading }] =
    useDeleteSubscription({
      refetchQueries: [],
      onCompleted: onDeleteSubscriptionCompleted,
      awaitRefetchQueries: false,
    });

  if (!userId) {
    return null;
  }

  const subscribers = task.subscribers || [];

  const isUserSubscribed = subscribers.reduce((prevValue, subscriber) => {
    return prevValue || subscriber.id === userId;
  }, false);

  const onClick = () => {
    if (createSubscriptionLoading || deleteSubscriptionLoading) {
      return;
    }

    const eventName = isUserSubscribed
      ? deleteSubscriptionEventName
      : createSubscriptionEventName;
    track({
      eventName,
      action: "click",
      element: "follow_task_link",
      props: {
        taskId,
      },
    });

    const variables = {
      resourceId: taskId,
      resourceType: NotificationResource.task,
      userId,
    };

    const mutation = isUserSubscribed ? deleteSubscription : createSubscription;
    mutation({ variables });
  };

  const followTitle = "Get notifications about activity on this task.";
  const unfollowTitle =
    "Stop getting notifications about activity on this task.";

  const title = isUserSubscribed ? unfollowTitle : followTitle;

  return (
    <StyledLink title={title} onClick={onClick}>
      {isUserSubscribed ? "Unfollow" : "Follow"}
    </StyledLink>
  );
};
